import React, { useState, useRef, useCallback, useEffect, useContext } from "react";
import { AutoTaggingResult, ClothingItem, SearchResultItem, VisualSearchResultsMap, ClothingItemSearchState } from "../../../utilities/types/unified-services";
import AppContext from "../../../utilities/contexts/appContext";
import SdkContext from "../../../utilities/contexts/sdk/sdkContext";
import { useLocation } from "react-router-dom";

interface VisualSearchResultsProps {
  result: AutoTaggingResult;
  uploadedImage?: string;
}

interface ImageDimensions {
  width: number;
  height: number;
}

interface ScaleFactor {
  x: number;
  y: number;
}

const VisualSearchResults: React.FC<VisualSearchResultsProps> = ({ result, uploadedImage }) => {
  const appContext = useContext(AppContext);
  const sdkContext = useContext(SdkContext);
  const location = useLocation();

  const isSDK = location.pathname.includes("/sdk/");
  const authToken = isSDK ? sdkContext.token : appContext.token;
  const [hoveredItem, setHoveredItem] = useState<ClothingItem | null>(null);
  const [selectedItem, setSelectedItem] = useState<ClothingItem | null>(null);
  const [searchResultsMap, setSearchResultsMap] = useState<VisualSearchResultsMap>({});
  const [currentSearchResults, setCurrentSearchResults] = useState<SearchResultItem[]>([]);
  const [isFirstServiceCalled, setIsFirstServiceCalled] = useState(false);
  const [imageDimensions, setImageDimensions] = useState<ImageDimensions | null>(null);
  const [scaleFactor, setScaleFactor] = useState<ScaleFactor | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const containerWidth = 240;
  const containerHeight = 400;

  const getImageSrc = (imageUrl: string) => {
    if (imageUrl.startsWith("blob:")) {
      return imageUrl;
    }

    if (imageUrl.startsWith("data:")) {
      return imageUrl;
    }

    return `data:image/jpeg;base64,${imageUrl}`;
  };

  const handleImageLoad = useCallback(() => {
    if (imageRef.current) {
      const img = imageRef.current;
      const originalWidth = img.naturalWidth;
      const originalHeight = img.naturalHeight;

      const scale = Math.min(containerWidth / originalWidth, containerHeight / originalHeight);

      setImageDimensions({
        width: originalWidth,
        height: originalHeight,
      });

      setScaleFactor({
        x: scale,
        y: scale,
      });
    }
  }, []);

  const getAdjustedCoordinates = useCallback(
    (region: ClothingItem["Region"]) => {
      if (!imageDimensions || !scaleFactor) return region;

      const scaledWidth = imageDimensions.width * scaleFactor.x;
      const scaledHeight = imageDimensions.height * scaleFactor.y;
      const offsetX = (containerWidth - scaledWidth) / 2;
      const offsetY = (containerHeight - scaledHeight) / 2;

      return {
        X1: ((region.X1 * scaleFactor.x + offsetX) / containerWidth) * 100,
        Y1: ((region.Y1 * scaleFactor.y + offsetY) / containerHeight) * 100,
        X2: ((region.X2 * scaleFactor.x + offsetX) / containerWidth) * 100,
        Y2: ((region.Y2 * scaleFactor.y + offsetY) / containerHeight) * 100,
      };
    },
    [imageDimensions, scaleFactor, containerWidth, containerHeight]
  );

  const getCenter = (region: ClothingItem["Region"]) => {
    const { X1, Y1, X2, Y2 } = getAdjustedCoordinates(region);
    return {
      x: (X1 + X2) / 2,
      y: (Y1 + Y2) / 2,
    };
  };

  const getInverseClipPath = (region: ClothingItem["Region"]) => {
    const { X1, Y1, X2, Y2 } = getAdjustedCoordinates(region);
    const radius = 1;
    return `polygon(
      0% 0%,
      100% 0%,
      100% 100%,
      0% 100%,
      0% 0%,

      ${X1}% ${Y1 + radius}%,
      ${X1}% ${Y2 - radius}%,
      ${X1 + radius}% ${Y2}%,
      ${X2 - radius}% ${Y2}%,
      ${X2}% ${Y2 - radius}%,
      ${X2}% ${Y1 + radius}%,
      ${X2 - radius}% ${Y1}%,
      ${X1 + radius}% ${Y1}%,
      ${X1}% ${Y1 + radius}% 
    )`;
  };

  const formatClothingType = (clotheType: string): string => {
    return clotheType.replace(/([A-Z])/g, " $1").trim();
  };

  const updateSearchResultsMap = (index: number, state: ClothingItemSearchState) => {
    setSearchResultsMap((prev) => ({
      ...prev,
      [index]: state,
    }));
  };

  const searchSimilarItems = useCallback(
    async (clothingItem: ClothingItem, index: number, page: number = 1) => {
      updateSearchResultsMap(index, {
        loading: true,
        error: null,
        results: searchResultsMap[index]?.results || [],
        pagination: {
          currentPage: page,
          totalPages: searchResultsMap[index]?.pagination?.totalPages || 1,
        },
      });

      try {
        const { Region, ...clothingData } = clothingItem;
        const searchPayload = {
          ...clothingData,
          ...Region,
          Country: "us",
          Count: 12,
          Page: page,
        };

        const response = await fetch("https://api.shaku.tech/api/v1/services/search", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken || ""}`,
          },
          body: JSON.stringify(searchPayload),
        });

        if (!response.ok) {
          throw new Error(`Failed to search similar items: ${response.statusText}`);
        }

        const searchData = await response.json();
        const searchResults = searchData.data?.Result || [];

        updateSearchResultsMap(index, {
          loading: false,
          error: null,
          results: searchResults,
          pagination: {
            currentPage: searchData.data?.current_page || 1,
            totalPages: searchData.data?.last_page || 1,
          },
        });

        if (selectedItem === clothingItem) {
          setCurrentSearchResults(searchResults);
        }
      } catch (error) {
        console.error("Error searching similar items:", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to search similar items";

        updateSearchResultsMap(index, {
          loading: false,
          error: errorMessage,
          results: [],
          pagination: {
            currentPage: 1,
            totalPages: 1,
          },
        });
      }
    },
    [appContext.token, selectedItem, searchResultsMap]
  );

  const searchAllSimilarItems = useCallback(async () => {
    if (!result?.Clothes?.length) return;

    result.Clothes.forEach((_, index) => {
      updateSearchResultsMap(index, {
        loading: true,
        error: null,
        results: [],
        pagination: { currentPage: 1, totalPages: 1 },
      });
    });

    if (result.Clothes[0]) {
      await searchSimilarItems(result.Clothes[0], 0);
    }

    const otherItemPromises = result.Clothes.slice(1).map((item, index) => searchSimilarItems(item, index + 1));

    await Promise.all(otherItemPromises);
  }, [result?.Clothes, searchSimilarItems]);

  useEffect(() => {
    if (result?.Clothes?.length && !isFirstServiceCalled) {
      setSelectedItem(result.Clothes[0]);
      searchAllSimilarItems();
      setIsFirstServiceCalled(true);
    }
  }, [result, isFirstServiceCalled, searchAllSimilarItems]);

  const handleItemSelect = (item: ClothingItem, index: number) => {
    setHoveredItem(item);
    setSelectedItem(item);

    const itemResults = searchResultsMap[index];
    if (itemResults && itemResults.results) {
      setCurrentSearchResults(itemResults.results);
    }
  };

  const handleItemPagination = async (page: number) => {
    if (!selectedItem) return;

    const selectedIndex = result.Clothes.findIndex((item) => item === selectedItem);
    if (selectedIndex !== -1) {
      await searchSimilarItems(selectedItem, selectedIndex, page);
    }
  };

  const calculateDiscountPercentage = (currentPrice: string, previousPrice: string | null): number | null => {
    if (!previousPrice) return null;
    const current = parseFloat(currentPrice.replace(/[^0-9.]/g, ""));
    const previous = parseFloat(previousPrice.replace(/[^0-9.]/g, ""));
    if (current >= previous) return null;
    return Math.round(((previous - current) / previous) * 100);
  };

  const CroppedImageDisplay: React.FC<{ clothingItem: ClothingItem }> = ({ clothingItem }) => {
    if (!uploadedImage || !imageDimensions || !scaleFactor) return null;

    const adjustedCoords = getAdjustedCoordinates(clothingItem.Region);
    const CONTAINER_SIZE = 60;

    const cropWidth = ((adjustedCoords.X2 - adjustedCoords.X1) * imageDimensions.width) / 100;
    const cropHeight = ((adjustedCoords.Y2 - adjustedCoords.Y1) * imageDimensions.height) / 100;

    const aspectRatio = cropWidth / cropHeight;

    let displayWidth = CONTAINER_SIZE;
    let displayHeight = CONTAINER_SIZE;

    if (aspectRatio > 1) {
      displayHeight = CONTAINER_SIZE / aspectRatio;
    } else {
      displayWidth = CONTAINER_SIZE * aspectRatio;
    }

    const scaleX = displayWidth / cropWidth;
    const scaleY = displayHeight / cropHeight;

    const scale = Math.max(scaleX, scaleY);

    const x = (adjustedCoords.X1 * imageDimensions.width) / 100;
    const y = (adjustedCoords.Y1 * imageDimensions.height) / 100;

    return (
      <div
        className="relative overflow-hidden rounded-md bg-gray-50"
        style={{
          width: `${CONTAINER_SIZE}px`,
          height: `${CONTAINER_SIZE}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: `${displayWidth}px`,
            height: `${displayHeight}px`,
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              width: `${imageDimensions.width}px`,
              height: `${imageDimensions.height}px`,
              backgroundImage: `url(${getImageSrc(uploadedImage)})`,
              backgroundSize: `${imageDimensions.width}px ${imageDimensions.height}px`,
              transform: `scale(${scale}) translate(${-x}px, ${-y}px)`,
              transformOrigin: "0 0",
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="w-full space-y-8">
      {/* <style>
        {`
          @keyframes infinitePulse {
            0%, 100% { transform: translate(-50%, -50%) scale(1); }
            50% { transform: translate(-50%, -50%) scale(1.2); }
          }
          .infinite-pulse {
            animation: infinitePulse 2s infinite ease-in-out;
          }
        `}
      </style> */}
      <div className="flex flex-col items-center w-full gap-4">
        {/* <div className="flex flex-col items-center w-full gap-4 md:items-start md:flex-row"> */}
        <div className="flex flex-col items-center w-full gap-4">
          <div className="flex flex-col items-center w-full gap-4 md:flex-row md:items-start">
            <div className="p-4 bg-white border border-gray-200 shadow-sm rounded-2xl max-w-fit">
              <div ref={containerRef} className="relative mx-auto w-fit">
                {!isFirstServiceCalled && (
                  <div className="absolute inset-0 z-50 grid place-content-center bg-black/50 rounded-xl">
                    <div className="w-20 h-20 border-4 border-white rounded-full border-t-transparent animate-spin"></div>
                  </div>
                )}

                <div className="grid place-content-center w-[15rem] h-[25rem] border-2 rounded-md border-gray-200 overflow-hidden bg-gray-50">
                  {uploadedImage ? (
                    <img ref={imageRef} src={getImageSrc(uploadedImage)} alt="Visual Clothes Search query" className="object-contain w-[15rem] h-[25rem]" onLoad={handleImageLoad} />
                  ) : (
                    <div className="grid w-[15rem] h-[25rem] place-content-center">
                      <div className="text-center text-gray-500">
                        <svg className="w-16 h-16 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <p className="text-sm">No image available</p>
                      </div>
                    </div>
                  )}
                </div>

                {result.Clothes.map((item, index) => {
                  const center = getCenter(item.Region);
                  const { X1, Y1, X2, Y2 } = getAdjustedCoordinates(item.Region);
                  const isItemDisabled = searchResultsMap[index]?.loading || false;
                  const hasResults = searchResultsMap[index]?.results?.length > 0;

                  return (
                    <div key={index}>
                      {!hoveredItem && (
                        <div
                          className={`absolute border-2 border-white rounded-full shadow-lg cursor-pointer transition-all duration-300 ${
                            isItemDisabled
                              ? "opacity-30 cursor-not-allowed bg-gray-400"
                              : hasResults
                              ? "opacity-50 bg-blue-400 hover:scale-150 infinite-pulse"
                              : "opacity-50 bg-blue-400 hover:scale-150 infinite-pulse"
                          }`}
                          style={{
                            left: `${center.x}%`,
                            top: `${center.y}%`,
                            width: "20px",
                            height: "20px",
                            transform: "translate(-50%, -50%)",
                          }}
                          onClick={() => !isItemDisabled && handleItemSelect(item, index)}
                          onMouseEnter={() => !isItemDisabled && setHoveredItem(item)}
                        />
                      )}

                      {hoveredItem === item && (
                        <div
                          className="absolute rounded-lg cursor-pointer ring-2 bg-opacity-30 ring-primary bg-primary"
                          style={{
                            left: `${X1}%`,
                            top: `${Y1}%`,
                            width: `${X2 - X1}%`,
                            height: `${Y2 - Y1}%`,
                          }}
                          onClick={() => handleItemSelect(item, index)}
                          onMouseLeave={() => setHoveredItem(null)}
                        />
                      )}
                    </div>
                  );
                })}

                {hoveredItem && (
                  <div
                    className="absolute inset-0 transition-opacity duration-300 bg-black pointer-events-none bg-opacity-70 rounded-xl"
                    style={{
                      clipPath: getInverseClipPath(hoveredItem.Region),
                    }}
                  />
                )}
              </div>
            </div>

            {result?.Clothes.length > 0 && (
              <div className="flex-grow w-full h-full bg-white border border-gray-200 shadow-sm md:min-h-[27rem] rounded-2xl">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="m-0 text-lg font-bold text-gray-800">Detected Items</h3>
                </div>
                <div className="grid h-full max-h-[23rem] grid-cols-1 gap-4 p-4 overflow-y-auto place-items-center sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 2xl:grid-cols-4">
                  {result.Clothes.map((item, index) => {
                    const isItemDisabled = searchResultsMap[index]?.loading;
                    const hasResults = searchResultsMap[index]?.results?.length > 0;

                    return (
                      <div
                        key={index}
                        className={`flex flex-col items-center gap-2 p-3 rounded-lg border h-full min-h-[8.125rem] transition-all duration-300 min-w-40 max-w-40 cursor-pointer ${
                          hoveredItem === item || selectedItem === item ? "bg-primary/10 border-primary/50" : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                        } ${isItemDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                        onClick={() => !isItemDisabled && handleItemSelect(item, index)}
                        onMouseEnter={() => !isItemDisabled && setHoveredItem(item)}
                        onMouseLeave={() => setHoveredItem(null)}
                      >
                        <CroppedImageDisplay clothingItem={item} />
                        <div className="text-center">
                          <div className="text-xs font-medium text-gray-800 line-clamp-1">{formatClothingType(item.ClotheType)}</div>
                          {isItemDisabled && <div className="mt-1 text-xs text-primary">Searching...</div>}
                          {!isItemDisabled && (
                            <div className={`mt-1 text-xs ${hasResults ? "text-primary" : "text-gray-500"}`}>{hasResults ? `${searchResultsMap[index]?.results?.length} found` : "Not found"}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="flex-1 w-full">
            <div className="bg-white border border-gray-200 shadow-sm rounded-2xl">
              <div className="p-4 border-b border-gray-200">
                <h2 className="m-0 text-lg font-bold text-gray-800">Similar Products</h2>
                {selectedItem && <p className="m-0 text-sm text-gray-600">Showing results for {formatClothingType(selectedItem.ClotheType)}</p>}
              </div>

              <div className="p-4">
                {!selectedItem ? (
                  <div className="flex flex-col items-center justify-center p-8 text-center">
                    <div className="flex items-center justify-center w-16 h-16 mb-4 bg-gray-100 rounded-full">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <p className="text-gray-600">Select a clothing item to find similar products</p>
                  </div>
                ) : (
                  (() => {
                    const selectedIndex = result.Clothes.findIndex((item) => item === selectedItem);
                    const currentItemState = selectedIndex !== -1 ? searchResultsMap[selectedIndex] : null;

                    if (currentItemState?.loading) {
                      return (
                        <div className="flex justify-center p-8">
                          <div className="w-8 h-8 border-4 border-blue-200 rounded-full border-t-blue-500 animate-spin"></div>
                        </div>
                      );
                    }

                    if (currentItemState?.error) {
                      return (
                        <div className="p-8 text-center">
                          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <p className="text-red-600">{currentItemState.error}</p>
                          <button
                            onClick={() => searchSimilarItems(selectedItem, selectedIndex)}
                            className="px-6 py-3 text-sm font-medium text-white transition-all duration-200 bg-red-500 border border-red-500 rounded-lg hover:bg-red-600"
                          >
                            Try Again
                          </button>
                        </div>
                      );
                    }

                    if (!currentSearchResults.length) {
                      return (
                        <div className="p-8 text-center">
                          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                          <p className="text-gray-600">No similar products found</p>
                        </div>
                      );
                    }

                    return (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 max-h-[600px] overflow-y-auto custom_scroll">
                          {currentSearchResults.map((item, index) => {
                            const discountPercentage = calculateDiscountPercentage(item.CurrentPrice, item.PreviousPrice);

                            return (
                              <a
                                href={item.Url}
                                key={index}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex flex-col overflow-hidden transition-all duration-300 bg-white border border-gray-200 rounded-lg hover:shadow-lg group"
                              >
                                <div className="relative overflow-hidden aspect-square">
                                  <img
                                    src={item.ImgUrl[0]}
                                    alt={item.Name}
                                    className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.src = "/images/icons/placeholder-image.svg";
                                    }}
                                  />
                                  {discountPercentage && <div className="absolute px-2 py-1 text-xs text-white bg-red-500 rounded top-2 right-2">-{discountPercentage}%</div>}
                                </div>

                                <div className="flex flex-col flex-1 gap-2 p-3">
                                  <div className="text-xs text-gray-600">{item.Brand}</div>
                                  <h3 className="flex-1 text-sm font-medium text-gray-800 line-clamp-2">{item.Name}</h3>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-bold text-gray-800">${item.CurrentPrice}</span>
                                    {item.PreviousPrice && item.CurrentPrice !== item.PreviousPrice && <span className="text-xs text-gray-500 line-through">${item.PreviousPrice}</span>}
                                  </div>
                                  <div className="text-xs text-gray-500">View on {item.Brand}</div>
                                </div>
                              </a>
                            );
                          })}
                        </div>

                        {(() => {
                          const selectedIndex = result.Clothes.findIndex((item) => item === selectedItem);
                          const paginationData = selectedIndex !== -1 ? searchResultsMap[selectedIndex]?.pagination : null;

                          if (paginationData && paginationData.totalPages > 1) {
                            return (
                              <div className="flex items-center justify-center pt-4 space-x-2 border-t border-gray-200">
                                <button
                                  onClick={() => handleItemPagination(paginationData.currentPage - 1)}
                                  disabled={paginationData.currentPage <= 1}
                                  className={` px-3 py-2 text-sm font-medium transition-all duration-200 border rounded-lg
                                        ${
                                          paginationData.currentPage <= 1
                                            ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed"
                                            : "border-gray-300 bg-white text-gray-700 hover:border-primary/50 hover:bg-primary/5"
                                        }`}
                                >
                                  Previous
                                </button>

                                <div className="flex space-x-1">
                                  {Array.from({ length: Math.min(5, paginationData.totalPages) }, (_, i) => {
                                    let pageNum;
                                    if (paginationData.totalPages <= 5) {
                                      pageNum = i + 1;
                                    } else {
                                      const current = paginationData.currentPage;
                                      if (current <= 3) {
                                        pageNum = i + 1;
                                      } else if (current >= paginationData.totalPages - 2) {
                                        pageNum = paginationData.totalPages - 4 + i;
                                      } else {
                                        pageNum = current - 2 + i;
                                      }
                                    }

                                    return (
                                      <button
                                        key={pageNum}
                                        onClick={() => handleItemPagination(pageNum)}
                                        className={`px-3 py-2 min-w-10 text-sm rounded-lg ${
                                          pageNum === paginationData.currentPage ? "bg-primary text-white" : "border border-gray-300 hover:bg-gray-50"
                                        }`}
                                      >
                                        {pageNum}
                                      </button>
                                    );
                                  })}
                                </div>

                                <button
                                  onClick={() => handleItemPagination(paginationData.currentPage + 1)}
                                  disabled={paginationData.currentPage >= paginationData.totalPages}
                                  className={` px-3 py-2 text-sm font-medium transition-all duration-200 border rounded-lg
                                        ${
                                          paginationData.currentPage >= paginationData.totalPages
                                            ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed"
                                            : "border-primary bg-primary text-white hover:bg-primary/90"
                                        }`}
                                >
                                  Next
                                </button>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisualSearchResults;
